import {yupResolver} from '@hookform/resolvers/yup';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {ScrollView, View} from 'react-native';
import {Divider, Text} from 'react-native-paper';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import {ShowToast} from '../Common/common';
import BTButton from '../Components/BTButton';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput as BTTextInput} from '../Components/BTTextInput';
import {useGetEmployeesQuery} from '../redux/services/emmemployeeApi';
import {
  useGetFOECheckinsQuery,
  useGetFOEOccRoomsQuery,
} from '../redux/services/foecheckinApi';
import {useInsertFOERoomShiftMutation} from '../redux/services/foeroomshiftApi';
import {useGetFOVacantRoomsQuery} from '../redux/services/foroomsApi';
import {RootState} from '../redux/store';
import styles from '../styles/FOStyles';

import T from '../Components/T';
const validationSchema = yup.object().shape({
  rs_checkin: yup.number().required('Folio No is required').moreThan(0),
  rs_froom: yup.number().required('From Room is required').moreThan(0),
  rs_troom: yup.number().required('To Room is required').moreThan(0),
  rs_status: yup.string().required('Room Status is required'),
  rs_employee: yup.number().required('Employee is required').moreThan(0),
  rs_narr: yup.string().required('Remarks is required').min(2).max(250),
});

const RoomShiftForm = ({navigation, route}: any) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;
  const [isSaving, setIsSaving] = useState(false);

  const {data: checkinData} = useGetFOECheckinsQuery(cid);
  const {data: occData} = useGetFOEOccRoomsQuery({cid: cid, coid: 0});
  const {data: vroomData} = useGetFOVacantRoomsQuery(cid);
  const {data: employeeData} = useGetEmployeesQuery(cid);

  const [insertRoomShift] = useInsertFOERoomShiftMutation();

  const initialValues: any = {
    rs_id: 0,
    rs_company: cid,
    rs_date: moment().format('YYYY/MM/DD HH:mm:ss'),
    rs_checkin: 0,
    rs_froom: 0,
    rs_troom: 0,
    rs_status: 'D',
    rs_employee: 0,
    rs_narr: '',
    rs_uid: gdata.user,
    rs_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    rs_muid: gdata.user,
    rs_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
  };

  const methods = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const checkin = checkinData?.map(v => ({
    id: v.ci_id,
    label: `${v.ci_no} - ${v.ab_name}`,
  }));

  const occRooms = occData?.map((v: any) => ({
    id: v.cd_room,
    label: `${v.rm_name} - ${v.ab_name}`,
  }));

  const vacantRooms = vroomData?.map((v: any) => ({
    id: v.rm_id,
    label: v.rm_name,
  }));

  const employees = employeeData?.map(v => ({
    id: v.em_id,
    label: v.em_name,
  }));

  const handleCancel = () => {
    methods.reset(initialValues);
    navigation.navigate('StatusScreen');
  };

  useEffect(() => {
    // Full reset (not per-field setValue) so amount/status/employee/remarks
    // left over from a previous room's unsaved entry don't carry over when
    // this same mounted screen instance is reused for a different room via
    // navigation.navigate('RoomShift', {room}). `checkin` was previously in
    // the deps array but is rebuilt via .map() every render (new reference
    // each time) and isn't even read in this effect - it only caused this
    // effect to refire on unrelated re-renders, not just on room changes.
    if (route.params?.room) {
      const selectedRoom = occData?.find(
        (v: any) => v.rm_id === Number(route.params.room),
      );
      methods.reset({
        ...initialValues,
        rs_froom: Number(route.params.room),
        rs_checkin: selectedRoom ? selectedRoom.ci_id : 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.room, occData]);

  const onSubmit: SubmitHandler<any> = async values => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      await insertRoomShift(values).unwrap();
      ShowToast('Room shift saved successfully');
      methods.reset(initialValues);
      navigation.navigate('StatusScreen');
    } catch (error: any) {
      ShowToast(error?.data?.message || error?.message || 'Failed to save room shift', 'error');
    }

    setIsSaving(false);
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
      <ScrollView
        contentContainerStyle={{
          padding: 8,
          paddingBottom: 80,
          flexGrow: 1,
          backgroundColor: '#f5f5f5',
        }}
        keyboardShouldPersistTaps="handled">
      <Text style={styles.header}><T>Room Shift</T></Text>
      <FormProvider {...methods}>
        <Row>
          <Col>
            <BTComboBox
              name="rs_checkin"
              label="Folio No"
              data={checkin}
              editable={false}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <BTComboBox
              name="rs_froom"
              label="From Room"
              data={occRooms}
              editable={false}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <BTComboBox name="rs_troom" label="To Room" data={vacantRooms} />
          </Col>
        </Row>
        <Row>
          <Col>
            <BTComboBox
              name="rs_status"
              label="Room Status"
              data={[
                {id: 'V', label: 'Vacant'},
                {id: 'D', label: 'Dirty'},
                {id: 'B', label: 'Block'},
                {id: 'M', label: 'Maintenance'},
              ]}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <BTComboBox name="rs_employee" label="Employee" data={employees} />
          </Col>
        </Row>
        <Row>
          <Col>
            <BTTextInput
              name="rs_narr"
              label="Remarks"
              multiline
              numberOfLines={3}
            />
          </Col>
        </Row>

        <Divider />
        <Row>
          <Col>
            <BTButton
              title="Save"
              onPress={methods.handleSubmit(onSubmit)}
              onLongPress={() => {}}
              color="#146C94"
              disabled={isSaving}
            />
          </Col>
          <Col>
            <BTButton
              title="Cancel"
              onPress={() => {
                methods.reset(initialValues);
                navigation.navigate('StatusScreen');
              }}
              onLongPress={() => {}}
              color="#146C94"
            />
          </Col>
        </Row>
      </FormProvider>
      </ScrollView>
    </View>
  );
};

export default RoomShiftForm;
